import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Download, Copy, CheckCircle, X, AlertTriangle, Link2, Globe } from 'lucide-react';
import { useSportsAuth, sportsApi } from '../SportsAuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// "Your Site Addresses" — the one home for a facility's two links (Site section):
//  1. PERMANENT link  /book/{slug} — never changes; what printed QR codes encode.
//     It auto-forwards visitors to the web address below when one is claimed.
//  2. WEB ADDRESS     {name}.agri-books.com — memorable, customizable (30-day
//     change limit), doubles as a /book/{name} alias server-side.
// Moved here from Settings (owner decision 2026-08-09).
export default function SiteAddresses() {
  const { organization } = useSportsAuth();
  const slug = organization?.slug || '';

  const [settings, setSettings] = useState(null);
  const [subdomainInput, setSubdomainInput] = useState('');
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [subdomainLocked, setSubdomainLocked] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [copied, setCopied] = useState(''); // 'perm' | 'web' | ''
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    sportsApi.get('/sports/settings').then(r => {
      const s = r.data || {};
      setSettings(s);
      if (s.custom_subdomain) setSubdomainInput(s.custom_subdomain);
      if (s.subdomain_last_changed_at) {
        const next = new Date(new Date(s.subdomain_last_changed_at).getTime() + 30 * 24 * 60 * 60 * 1000);
        if (next > new Date()) setSubdomainLocked(next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
      }
    }).catch(() => setSettings({}));
  }, []);

  const subdomain = settings?.custom_subdomain || '';
  // The permanent link always presents the production apex — QR codes printed
  // from a dev tunnel must still point customers at the real site.
  const apex = window.location.hostname.endsWith('agri-books.com') ? 'https://agri-books.com' : window.location.origin;
  const permUrl = slug ? `${apex}/book/${slug}` : '';
  const webUrl = subdomain ? `https://${subdomain}.agri-books.com` : '';

  const subdomainValid = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(subdomainInput);
  const subdomainChanged = subdomainInput !== subdomain;

  // Live availability check — debounced 450ms
  useEffect(() => {
    if (!subdomainInput || !subdomainValid || !subdomainChanged) { setAvailability(null); return; }
    setAvailability('checking');
    const timer = setTimeout(() => {
      fetch(`${BACKEND}/api/sports/subdomain-check?subdomain=${subdomainInput}`)
        .then(r => r.json())
        .then(d => setAvailability(d.available ? 'available' : d.reason))
        .catch(() => setAvailability(null));
    }, 450);
    return () => clearTimeout(timer);
  }, [subdomainInput, subdomainValid, subdomainChanged]);

  const handleSave = async () => {
    setShowWarning(false);
    setSubdomainSaving(true);
    try {
      const res = await sportsApi.put('/sports/settings/subdomain', { subdomain: subdomainInput });
      toast.success(`Web address set! ${res.data.url || `https://${subdomainInput}.agri-books.com`}`);
      setSettings(s => ({ ...s, custom_subdomain: subdomainInput, subdomain_last_changed_at: new Date().toISOString() }));
      const next = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      setSubdomainLocked(next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update the web address');
    } finally { setSubdomainSaving(false); }
  };

  const copy = (text, which) => {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(which); setTimeout(() => setCopied(''), 2000); })
      .catch(() => toast.error('Could not copy — long-press the link instead'));
  };

  const downloadQR = () => {
    const svg = document.querySelector('#site-addresses-qr svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 600; canvas.height = 640;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 600, 640);
      ctx.drawImage(img, 0, 0, 600, 600);
      // Caption = the memorable address when claimed (that's where scans land);
      // the QR itself encodes the PERMANENT link so reprints are never needed.
      ctx.fillStyle = '#0A0A0A';
      ctx.font = 'bold 22px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(subdomain ? `${subdomain}.agri-books.com` : permUrl.replace('https://', ''), 300, 622);
      const link = document.createElement('a');
      link.download = `${subdomain || slug}-booking-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code downloaded');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (!settings) return null;
  const inp = { background: '#0A0A0A', border: 'none', color: '#fff', padding: '10px 12px', outline: 'none', fontSize: 14 };

  return (
    <div data-testid="site-addresses" className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
          <div className="rounded-xl w-full max-w-sm p-5" style={{ background: '#141414', border: '1px solid #2A2A2A' }}>
            <p className="font-bold text-sm mb-2" style={{ color: '#FFB300' }}>Change your web address?</p>
            <p className="text-xs mb-4" style={{ color: '#A3A3A3', lineHeight: 1.6 }}>
              Anyone using <strong style={{ color: '#fff' }}>{subdomain}.agri-books.com</strong> will lose it, and you can't change again for 30 days.
              Your permanent link and printed QR codes keep working — they'll forward to the new address.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowWarning(false)} className="flex-1 py-2.5 rounded-lg text-sm font-bold"
                style={{ background: '#1A1A1A', color: '#A3A3A3' }}>
                Cancel
              </button>
              <button onClick={handleSave} className="flex-1 py-2.5 rounded-lg text-sm font-bold"
                style={{ background: '#E63946', color: '#fff' }}>
                Change Address
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
        <Globe className="h-4 w-4" style={{ color: '#E63946' }} />
        <h2 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Your Site Addresses</h2>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Permanent link + QR ── */}
        <div data-testid="site-permanent-link">
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: '#E63946' }}>
            Permanent link — use this for printed QR codes
          </p>
          <p className="text-xs mb-3" style={{ color: '#555', lineHeight: 1.6 }}>
            This link never changes, so a printed QR code never expires.
            {subdomain
              ? ' Put it on posters, tarpaulins and flyers — it always forwards visitors to your web address.'
              : ' Put it on posters, tarpaulins and flyers — it keeps working even if you claim a web address later.'}
          </p>
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-3" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
            <span className="text-sm font-bold flex-1 truncate" style={{ color: '#fff' }}>{permUrl}</span>
            <button onClick={() => copy(permUrl, 'perm')} data-testid="site-copy-permanent"
              className="flex items-center gap-1 text-xs px-2 py-1 rounded shrink-0" style={{ color: '#A3A3A3', background: '#1A1A1A', border: 'none', cursor: 'pointer' }}>
              <Copy className="h-3 w-3" /> {copied === 'perm' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="text-center rounded-xl p-4" style={{ background: '#0A0A0A', border: '1px solid #1E1E1E' }}>
            <div id="site-addresses-qr" style={{ background: '#fff', borderRadius: 12, padding: 12, display: 'inline-block' }}>
              <QRCodeSVG value={permUrl} size={160} bgColor="#ffffff" fgColor="#0A0A0A" level="H" includeMargin={true} />
            </div>
            <p className="text-xs mt-2 font-bold" style={{ color: '#E63946' }}>
              {subdomain ? `${subdomain}.agri-books.com` : permUrl.replace('https://', '')}
            </p>
            <button onClick={downloadQR} data-testid="site-download-qr"
              className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 rounded-lg text-xs font-bold uppercase"
              style={{ background: '#E63946', color: '#fff', border: 'none', cursor: 'pointer' }}>
              <Download className="h-3.5 w-3.5" /> Download QR Code
            </button>
            <p className="text-xs mt-2" style={{ color: '#555' }}>
              Safe to print — this code never needs reprinting.
            </p>
          </div>
        </div>

        {/* ── Web address (custom subdomain) ── */}
        <div data-testid="site-web-address">
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: '#00E676' }}>
            Web address — share this with customers
          </p>
          <p className="text-xs mb-3" style={{ color: '#555', lineHeight: 1.6 }}>
            This is your facility's own web address. Send it to customers, post it on social
            media — easy to remember, easy to type. Your permanent link forwards here.
          </p>

          {subdomain && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-3" style={{ background: '#0A1A0A', border: '1px solid #00E67630' }}>
              <CheckCircle className="h-4 w-4 shrink-0" style={{ color: '#00E676' }} />
              <span className="text-sm font-bold flex-1 truncate" style={{ color: '#00E676' }}>{subdomain}.agri-books.com</span>
              <button onClick={() => copy(webUrl, 'web')} data-testid="site-copy-web"
                className="flex items-center gap-1 text-xs px-2 py-1 rounded shrink-0" style={{ color: '#555', background: '#1A2A1A', border: 'none', cursor: 'pointer' }}>
                <Copy className="h-3 w-3" /> {copied === 'web' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}

          {subdomainLocked && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-3" style={{ background: '#1A1A0A', border: '1px solid #FFB30030' }}>
              <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: '#FFB300' }} />
              <p className="text-xs" style={{ color: '#FFB300' }}>
                Address locked — next change available <strong>{subdomainLocked}</strong>
              </p>
            </div>
          )}

          {!subdomainLocked && (
            <div>
              <div className="flex items-center gap-0 rounded-lg overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
                <div className="px-3 py-2.5 text-sm shrink-0" style={{ background: '#0A0A0A', color: '#555', borderRight: '1px solid #2A2A2A' }}>https://</div>
                <input data-testid="site-subdomain-input" value={subdomainInput}
                  onChange={e => setSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="yourfacility" maxLength={30} style={{ ...inp, flex: 1, minWidth: 0 }} />
                <div className="px-3 py-2.5 text-sm shrink-0" style={{ background: '#0A0A0A', color: '#555', borderLeft: '1px solid #2A2A2A' }}>.agri-books.com</div>
              </div>
              {subdomainInput.length > 0 && !subdomainValid && (
                <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: '#E63946' }}>
                  <X className="h-3 w-3 inline" /> 3–30 chars, lowercase, numbers and dashes only
                </p>
              )}
              {subdomainValid && subdomainChanged && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs" style={{ minHeight: 18 }}>
                  {availability === 'checking' && (<><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #555', borderTopColor: '#ccc', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /><span style={{ color: '#737373' }}>Checking…</span></>)}
                  {availability === 'available' && (<><CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: '#00E676' }} /><span style={{ color: '#00E676' }}>Available — <strong>https://{subdomainInput}.agri-books.com</strong></span></>)}
                  {availability === 'taken' && (<><X className="h-3.5 w-3.5 shrink-0" style={{ color: '#E63946' }} /><span style={{ color: '#E63946' }}>Already taken — try a different name</span></>)}
                  {availability === 'reserved' && (<><X className="h-3.5 w-3.5 shrink-0" style={{ color: '#E63946' }} /><span style={{ color: '#E63946' }}>Reserved name — choose something else</span></>)}
                  {availability === null && (<span style={{ color: '#555' }}>https://{subdomainInput}.agri-books.com</span>)}
                </div>
              )}
              <button
                onClick={() => { if (subdomain && subdomainChanged) setShowWarning(true); else handleSave(); }}
                disabled={subdomainSaving || !subdomainValid || !subdomainChanged || ['taken', 'reserved', 'checking'].includes(availability)}
                data-testid="site-subdomain-save"
                className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm uppercase"
                style={{
                  background: (subdomainValid && subdomainChanged && availability === 'available') ? '#E63946' : '#1A1A1A',
                  color: (subdomainValid && subdomainChanged && availability === 'available') ? '#fff' : '#555',
                  border: 'none',
                  cursor: (subdomainValid && subdomainChanged && availability === 'available' && !subdomainSaving) ? 'pointer' : 'not-allowed',
                  opacity: subdomainSaving ? 0.7 : 1,
                }}>
                <Link2 className="h-3.5 w-3.5" />
                {subdomainSaving ? 'Saving…' : subdomain ? 'Update Address' : 'Claim Address'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
